import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Palmtree, Clock, Thermometer, Megaphone, BarChart3, Bell, CalendarDays,
  Users, Palette, ScrollText, UserCircle, Menu, LogOut, Moon, Sun, Handshake, Network, Globe2,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, assetUrl } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import { can, ROLE_LABEL } from "@/lib/perm";

const MAIN = [
  ["/", "Dashboard", LayoutDashboard, "dashboard"],
  ["/ferie", "Ferie", Palmtree, "ferie"],
  ["/permessi", "Permessi", Clock, "permessi"],
  ["/malattie", "Malattie", Thermometer, "malattie"],
  ["/comunicazioni", "Comunicazioni", Megaphone, "comunicazioni"],
  ["/sondaggi", "Sondaggi", BarChart3, "sondaggi"],
  ["/incontri", "Incontri", Handshake, "incontri"],
  ["/notifiche", "Notifiche", Bell, "notifiche"],
];
const ADMIN = [
  ["/calendario", "Calendario assenze", CalendarDays, "calendario"],
  ["/dipendenti", "Dipendenti", Users, "dipendenti"],
  ["/personalizzazione", "Personalizzazione", Palette, "personalizzazione"],
  ["/registro", "Registro attività", ScrollText, "registro"],
];

function NavItem({ to, label, Icon, id, badge, onClick }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      onClick={onClick}
      data-testid={`sidebar-link-${id}`}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-150",
          isActive ? "bg-white/10 text-white" : "text-white/65 hover:bg-white/5 hover:text-white"
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn("h-4 w-4", isActive && "text-[hsl(var(--primary))]")} />
          <span className="flex-1">{label}</span>
          {badge > 0 && (
            <span className="rounded-full bg-[hsl(var(--primary))] px-1.5 text-[11px] font-semibold text-white" data-testid={`sidebar-badge-${id}`}>
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function SidebarContent({ unread, netUnread, onNavigate }) {
  const { user, settings, logout } = useAuth();
  return (
    <div className="grain flex h-full flex-col bg-[var(--brand-bg)] text-white">
      <div className="flex items-center gap-3 px-5 py-6">
        {settings?.logo_url ? (
          <img src={assetUrl(settings.logo_url)} alt="logo" className="h-9 w-9 rounded-md bg-white object-contain p-1" />
        ) : (
          <div className="font-display flex h-9 w-9 items-center justify-center rounded-md bg-[hsl(var(--primary))] text-lg font-extrabold">
            {(settings?.app_name || "N")[0]}
          </div>
        )}
        <div className="min-w-0">
          <p className="font-display truncate text-base font-bold" data-testid="sidebar-app-name">{settings?.app_name}</p>
          <p className="truncate text-xs text-white/50">{settings?.company_name || "Gestione staff"}</p>
        </div>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3">
        {(user.role === "admin" ? [MAIN[0], ["/rete", "Rete", Network, "rete"], ...MAIN.slice(1)] : MAIN).map(([to, l, I, id]) => (
          <NavItem key={to} to={to} label={l} Icon={I} id={id} badge={id === "notifiche" ? unread : 0} onClick={onNavigate} />
        ))}
        {user.role === "admin" && (
          <>
            <p className="px-3 pb-1.5 pt-6 text-[11px] font-semibold uppercase tracking-widest text-white/35">Amministrazione</p>
            {ADMIN.map(([to, l, I, id]) => <NavItem key={to} to={to} label={l} Icon={I} id={id} onClick={onNavigate} />)}
            <p className="px-3 pb-1.5 pt-6 text-[11px] font-semibold uppercase tracking-widest text-white/35">Tra soci</p>
            <NavItem to="/network-soci" label="Network Soci" Icon={Globe2} id="network-soci" badge={netUnread} onClick={onNavigate} />
          </>
        )}
        {user.role === "manager" && can(user, "view_calendar") && (
          <>
            <p className="px-3 pb-1.5 pt-6 text-[11px] font-semibold uppercase tracking-widest text-white/35">Responsabile</p>
            <NavItem to="/calendario" label="Calendario assenze" Icon={CalendarDays} id="calendario" onClick={onNavigate} />
          </>
        )}
      </nav>
      <div className="border-t border-white/10 p-3">
        <NavItem to="/profilo" label={user.name} Icon={UserCircle} id="profilo" onClick={onNavigate} />
        <button
          onClick={logout}
          data-testid="sidebar-logout-button"
          className="mt-0.5 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-white/65 transition-colors hover:bg-white/5 hover:text-white"
        >
          <LogOut className="h-4 w-4" /> Esci
        </button>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, setUser, settings } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [netUnread, setNetUnread] = useState(0);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const load = () => {
      api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
      if (user.role === "admin") api.get("/network/unread").then((r) => setNetUnread(r.data.total)).catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [loc.pathname, user.role]);

  const isDark = document.documentElement.classList.contains("dark");
  const toggleTheme = async () => {
    const { data } = await api.put("/me", { theme: isDark ? "light" : "dark" });
    setUser(data);
  };

  return (
    <div className="flex min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block" data-testid="desktop-sidebar">
        <SidebarContent unread={unread} netUnread={netUnread} />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 border-0 p-0">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SidebarContent unread={unread} netUnread={netUnread} onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b bg-background/85 px-4 backdrop-blur-md sm:px-8">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
          <p className="font-display text-sm font-semibold lg:hidden">{settings?.app_name}</p>
          <div className="ml-auto flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-button" aria-label="Tema">
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="icon" className="relative" onClick={() => nav("/notifiche")} data-testid="header-notifications-button">
              <Bell className="h-4 w-4" />
              {unread > 0 && (
                <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary" data-testid="header-unread-dot" />
              )}
            </Button>
            <button onClick={() => nav("/profilo")} className="ml-2 flex items-center gap-2.5" data-testid="header-profile-button">
              <UserAvatar fileId={user.avatar_file_id} name={user.name} testId="header-avatar" />
              <span className="hidden text-left sm:block">
                <span className="block text-sm font-medium leading-tight">{user.name}</span>
                <span className="block text-xs text-muted-foreground">{ROLE_LABEL[user.role] || "Dipendente"}</span>
              </span>
            </button>
          </div>
        </header>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-8 lg:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
